import React, { useState, useEffect } from 'react';
import './MortgageCalculator.css';

const MortgageCalculator = ({ propertyPrice = 0 }) => {
  const [price, setPrice] = useState(propertyPrice);
  const [downPayment, setDownPayment] = useState(propertyPrice * 0.2); // Default 20%
  const [interestRate, setInterestRate] = useState(6.5); // Default interest rate
  const [loanTerm, setLoanTerm] = useState(30); // Default 30 years
  const [monthlyPayment, setMonthlyPayment] = useState(0);

  useEffect(() => {
    setPrice(propertyPrice);
    setDownPayment(propertyPrice * 0.2);
  }, [propertyPrice]);

  useEffect(() => {
    calculateMonthlyPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price, downPayment, interestRate, loanTerm]);

  const calculateMonthlyPayment = () => {
    const principal = price - downPayment;
    if (principal <= 0 || interestRate <= 0 || loanTerm <= 0) {
      setMonthlyPayment(0);
      return;
    }

    const monthlyRate = interestRate / 100 / 12;
    const numberOfPayments = loanTerm * 12;

    const payment =
      (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

    setMonthlyPayment(payment);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const loanAmount = Math.max(0, price - downPayment);
  const totalPayment = monthlyPayment * loanTerm * 12;
  const totalInterest = Math.max(0, totalPayment - loanAmount);
  const downPaymentPercent = price > 0 ? Math.min(100, ((downPayment / price) * 100)).toFixed(1) : 0;

  return (
    <div className="mortgage-calculator">
      <h3>Calculadora de Hipoteca</h3>
      
      <div className="calculator-inputs">
        <div className="input-group">
          <label htmlFor="price">Precio de la Propiedad</label>
          <input
            type="number"
            id="price"
            value={price}
            onChange={(e) => setPrice(Number(e.target.value))}
            min="0"
          />
        </div>

        <div className="input-group">
          <label htmlFor="downPayment">
            Pago Inicial (Down Payment) ({downPaymentPercent}%)
          </label>
          <input
            type="number"
            id="downPayment"
            value={downPayment}
            onChange={(e) => setDownPayment(Number(e.target.value))}
            min="0"
            max={price}
          />
        </div>

        <div className="input-group">
          <label htmlFor="interestRate">Tasa de Interés (%)</label>
          <input
            type="number"
            id="interestRate"
            value={interestRate}
            onChange={(e) => setInterestRate(Number(e.target.value))}
            min="0"
            max="100"
            step="0.1"
          />
        </div>

        <div className="input-group">
          <label htmlFor="loanTerm">Plazo del Préstamo (años)</label>
          <select
            id="loanTerm"
            value={loanTerm}
            onChange={(e) => setLoanTerm(Number(e.target.value))}
          >
            <option value="10">10 años</option>
            <option value="15">15 años</option>
            <option value="20">20 años</option>
            <option value="30">30 años</option>
          </select>
        </div>
      </div>

      <div className="calculator-results">
        <div className="result-main">
          <div className="result-label">Pago Mensual Estimado</div>
          <div className="result-value">{formatCurrency(monthlyPayment)}</div>
        </div>

        <div className="result-breakdown">
          <div className="breakdown-item">
            <span className="breakdown-label">Monto del Préstamo:</span>
            <span className="breakdown-value">{formatCurrency(loanAmount)}</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">Pago Total:</span>
            <span className="breakdown-value">{formatCurrency(totalPayment)}</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">Interés Total:</span>
            <span className="breakdown-value">{formatCurrency(totalInterest)}</span>
          </div>
        </div>

        <div className="calculator-disclaimer">
          <p>
            * Esta es una estimación. Los pagos reales pueden variar según los impuestos,
            seguros y otros costos.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MortgageCalculator;
